import { CSSProperties, useState } from "react";
import { usePostponeExpeditionBo } from "@/api/expedition.api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  GetAllExpeditionBoResponseDtoExpedition,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { PickupTimeCotationEnum } from "@/constants/enums/pickupTimeCotation.enum";
import { startOfDay } from "date-fns";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePickupSlotOptions } from "@/hooks/useEnumOptions";

type SingleProps = {
  expedition: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto;
  expeditions?: never;
};

type BulkProps = {
  expeditions: GetAllExpeditionBoResponseDtoExpedition[];
  expedition?: never;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
} & (SingleProps | BulkProps);

const PICKUP_SLOT_COLORS: Record<PickupTimeCotationEnum, { selected: string; unselected: string }> =
  {
    [PickupTimeCotationEnum.MORNING]: {
      selected: "bg-green-500 text-white hover:bg-green-500/90",
      unselected: "hover:bg-green-500/10",
    },
    [PickupTimeCotationEnum.AFTERNOON]: {
      selected: "bg-yellow-400 text-white hover:bg-yellow-400/90",
      unselected: " hover:bg-yellow-400/10",
    },
  };

const PICKUP_SLOT_CALENDAR_VARS: Record<PickupTimeCotationEnum, CSSProperties> = {
  [PickupTimeCotationEnum.MORNING]: {
    "--primary": "var(--color-green-500)",
    "--primary-foreground": "var(--color-white)",
  } as CSSProperties,
  [PickupTimeCotationEnum.AFTERNOON]: {
    "--primary": "var(--color-yellow-400)",
    "--primary-foreground": "var(--color-white)",
  } as CSSProperties,
};

const getExistingPickupDate = (
  expedition: GetAllExpeditionBoResponseDtoExpedition | GetOneExpeditionBoResponseDto,
): Date | undefined => {
  const raw =
    "pickupDate" in expedition && expedition.pickupDate
      ? expedition.pickupDate
      : expedition.pickupInfo?.pickupDate;
  return raw ? new Date(raw) : undefined;
};

export const PickupDateModal = ({ expedition, expeditions, open, onClose, onSuccess }: Props) => {
  const { t } = useTranslation(["shared", "common"]);
  const pickupSlotOptions = usePickupSlotOptions();
  const today = startOfDay(new Date());
  const isBulk = !!expeditions;
  const allExpeditions = isBulk
    ? expeditions
    : expedition
      ? [expedition as GetAllExpeditionBoResponseDtoExpedition]
      : [];

  const [pickupDateValue, setPickupDateValue] = useState<Date>(() => {
    if (isBulk) return today;
    const existing = expedition ? getExistingPickupDate(expedition) : undefined;
    return existing && existing >= today ? existing : today;
  });

  const [pickupSlotValue, setPickupSlotValue] = useState<PickupTimeCotationEnum>(() => {
    if (isBulk) return PickupTimeCotationEnum.MORNING;
    const slot =
      expedition?.pickupInfo && "pickupSlot" in expedition.pickupInfo
        ? expedition.pickupInfo.pickupSlot
        : undefined;
    return slot && slot in PickupTimeCotationEnum
      ? (slot as PickupTimeCotationEnum)
      : PickupTimeCotationEnum.MORNING;
  });

  const [isPending, setIsPending] = useState(false);
  const { mutateAsync: postponeExpeditionBo } = usePostponeExpeditionBo();
  const isMobile = useIsMobile();

  const handleConfirm = async () => {
    if (!pickupDateValue) return;
    setIsPending(true);
    try {
      const noon = new Date(pickupDateValue);
      noon.setHours(12, 0, 0, 0);
      await Promise.all(
        allExpeditions.map((exp) =>
          postponeExpeditionBo({
            id: exp.id,
            payload: { pickupDate: noon.toISOString(), pickupSlot: pickupSlotValue },
          }),
        ),
      );
      onSuccess?.();
      onClose();
    } finally {
      setIsPending(false);
    }
  };

  const title = isBulk ? t("pickupDateModal.bulkTitle") : t("pickupDateModal.title");
  const description = isBulk
    ? t("pickupDateModal.bulkDescription", { count: allExpeditions.length })
    : t("pickupDateModal.description");

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-fit" aria-describedby="pickup-date-description">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription id="pickup-date-description">{description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <div style={PICKUP_SLOT_CALENDAR_VARS[pickupSlotValue]} className="self-center">
            <Calendar
              mode="single"
              selected={pickupDateValue}
              onSelect={(date) => date && setPickupDateValue(date)}
              defaultMonth={pickupDateValue}
              numberOfMonths={isMobile ? 1 : 2}
              fixedWeeks
              showOutsideDays={false}
              required
              className="pointer-events-auto rounded-md border"
            />
          </div>
          <div className="bg-muted mb-4 flex gap-1 self-center rounded-lg p-1">
            {pickupSlotOptions.map(({ label, value }) => (
              <Button
                key={value}
                type="button"
                variant={pickupSlotValue === value ? "default" : "ghost"}
                size="sm"
                onClick={() => setPickupSlotValue(value)}
                className={cn(
                  "w-[150px] flex-1 shadow-none",
                  pickupSlotValue === value
                    ? PICKUP_SLOT_COLORS[value].selected
                    : PICKUP_SLOT_COLORS[value].unselected,
                )}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1" disabled={isPending}>
            {t("common:actions.cancel")}
          </Button>
          <Button
            variant="default"
            onClick={handleConfirm}
            className="flex-1"
            disabled={isPending || !pickupDateValue}
          >
            {isPending ? <Loader2 className="animate-spin" /> : t("pickupDateModal.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
